/** @jsxImportSource @emotion/react */
import Input from 'components/atoms/Input'
import TextArea from 'components/atoms/Textarea'
import React from 'react'
import { Button, Modal } from 'react-bootstrap'
import ReactSelect from 'react-select'
import { toast } from 'react-toastify'
import { useDebounce } from 'use-debounce'
import { useApplyBillingPrepaymentMutation } from 'store/services/billingPrepayments'
import { useGetPatientBillingChargesQuery } from 'store/services/billingCharges'
import { getChargeDropdownLabel } from '../billingCharge/utils'

type Props = {
  open: boolean
  onClose: () => void
  patientId: number
  prepaymentId: string
  onDone?: () => void
  maxAmount?: number
}

export const ApplyPrepaymentModal: React.FC<Props> = ({
  open,
  onClose,
  patientId,
  prepaymentId,
  onDone,
  maxAmount,
}) => {
  const [amount, setAmount] = React.useState<string>('')
  const [notes, setNotes] = React.useState<string>('')
  const [search, setSearch] = React.useState<string>('')
  const [debounced] = useDebounce(search, 300)

  const { data: charges = [], refetch } = useGetPatientBillingChargesQuery(
    {
      patientId,
    },
    { skip: !open, refetchOnMountOrArgChange: true },
  )
  const [applyPrepayment, { isLoading }] = useApplyBillingPrepaymentMutation()

  const paymentOptions = React.useMemo(() => {
    const items = (charges || []).filter((charge) => {
      const hasRemainingBalance =
        Number(charge.currentPatientBalance || 0) > 0 || Number(charge.currentPlanBalance || 0) > 0
      const matchesSearch = !debounced || String(charge.id).includes(debounced)
      return charge.balanceStatus === 'ACTIVE' && hasRemainingBalance && matchesSearch
    })
    return items.map((p) => ({
      value: p.id,
      label: getChargeDropdownLabel(p),
    }))
  }, [charges, debounced])

  const [selectedPayment, setSelectedPayment] = React.useState<string | undefined>(undefined)

  const selectedPaymentDetails = React.useMemo(() => {
    if (!selectedPayment) {
      return undefined
    }

    return (charges || []).find((p) => String(p.id) === String(selectedPayment))
  }, [charges, selectedPayment])

  const prepaymentRemaining = React.useMemo(() => Number(maxAmount ?? 0), [maxAmount])

  const paymentBalance = React.useMemo(() => {
    if (!selectedPaymentDetails) {
      return 0
    }

    return Number(
      (
        Number(selectedPaymentDetails.currentPatientBalance || 0)
        + Number(selectedPaymentDetails.currentPlanBalance || 0)
      ).toFixed(2),
    )
  }, [selectedPaymentDetails])

  const allowedAmount = React.useMemo(() => {
    if (!selectedPaymentDetails) {
      return 0
    }

    const allowed = Math.min(paymentBalance, prepaymentRemaining || 0)
    return allowed > 0 ? Number(allowed.toFixed(2)) : 0
  }, [paymentBalance, prepaymentRemaining, selectedPaymentDetails])

  React.useEffect(() => {
    setAmount('')
  }, [selectedPayment])

  React.useEffect(() => {
    if (open) {
      refetch()
    }
  }, [open, refetch])

  const onSubmit = async () => {
    try {
      const num = Number(amount)
      if (!selectedPayment) {
        toast.error('Select charge')
        return
      }
      if (!num || num <= 0) {
        toast.error('Enter valid amount')
        return
      }

      if (allowedAmount <= 0 || num > allowedAmount) {
        toast.error('Amount exceeds available balance')
        return
      }

      await applyPrepayment({
        patientId,
        prePaymentId: prepaymentId,
        chargeId: selectedPayment,
        amount: num,
        note: notes?.trim() || undefined,
      }).unwrap()
      toast.success('Prepayment applied')
      if (typeof onDone === 'function') onDone()
      onClose()
    } catch (e: any) {
      toast.error(e?.data?.message || 'Failed to apply prepayment')
    }
  }

  return (
    <Modal
      show={open}
      onHide={onClose}
      css={{
        '.modal-content': { borderRadius: 15 },
        '.modal-body': { padding: '20px 30px' },
      }}
      centered>
      <div css={{ textAlign: 'center', padding: '20px 0', fontWeight: 600, fontSize: 22 }}>
        Apply Prepayment
      </div>
      <Modal.Body>
        <div className="row">
          <div className="col-12">
            <label className="textGray">Charge</label>
            <ReactSelect
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 1051 }) }}
              className="basic-single"
              classNamePrefix="select"
              isClearable
              isSearchable
              placeholder="Search Charge"
              name="chargeId"
              options={paymentOptions}
              onInputChange={(v) => setSearch(v)}
              inputValue={search}
              onChange={(item) => setSelectedPayment(item ? String(item.value) : undefined)}
            />
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-6">
            <label className="textGray">Amount to Apply</label>
            <div className="d-flex">
              <Input
                value={amount}
                name="amount"
                placeholder="$0.00"
                type="number"
                step="0.01"
                minValue={'0'}
                maxValue={selectedPayment ? allowedAmount.toFixed(2) : undefined}
                handleChange={(e) => {
                  const value = e.target.value

                  if (!selectedPayment) {
                    setAmount(value)
                    return
                  }

                  const numericValue = Number(value)

                  if (value === '' || Number.isNaN(numericValue)) {
                    setAmount('')
                    return
                  }

                  if (numericValue < 0) {
                    setAmount('0')
                    return
                  }

                  if (allowedAmount > 0 && numericValue > allowedAmount) {
                    setAmount(allowedAmount.toFixed(2))
                    return
                  }

                  setAmount(value)
                }}
                onBlur={(e) => {
                  const value = e.currentTarget.value
                  const numericValue = Number(value)

                  if (value === '' || Number.isNaN(numericValue)) {
                    setAmount('')
                    return
                  }

                  setAmount(numericValue.toFixed(2))
                }}
                changeOnBlur={false}
                className="form-control"
                disabled={!selectedPayment}
                parentDiv={null}
              />
              <Button
                variant={'outline-secondary ml-1'}
                onClick={() => {
                  const v = selectedPayment ? allowedAmount : 0
                  setAmount(v > 0 ? v.toFixed(2) : '')
                }}
                disabled={!selectedPayment || allowedAmount <= 0}
              >
                Max
              </Button>
            </div>
            {selectedPayment && (
              <div className="text-muted" css={{ fontSize: 12, marginTop: 4 }}>
                Max amount: ${allowedAmount.toFixed(2)}
              </div>
            )}
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-12">
            <TextArea
              handleChange={(e) => setNotes(e.target.value)}
              name="notes"
              label="Notes"
              labelClassName="text-gray"
              value={notes}
              rows={5}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-end">
        <Button variant="outline-secondary" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button className="ml-2" variant="primary" onClick={onSubmit} disabled={isLoading}>
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
